import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { useCourtFilingMarks } from "components/CourtFilingMarks";
import FileUpload from "components/FileUpload";
import DocumentViewerModal from "components/DocumentViewer";
import {
  CaseCreation,
  CourtApplication,
  CourtApplicationStatus,
  FILE_URL,
} from "types";
import { DateView, errorHandlerOnForm } from "utils/helper";
import {
  addCourtApplication,
  addCourtApplicationFeedback,
  addCourtApplicationRespondBack,
  editCourtApplication,
  fetchCourtApplications,
  updateCourtApplication,
} from "services/courtCaseService";

type Props = {
  caseData: CaseCreation;
  isProsecutor?: boolean;
  isCoordinator?: boolean;
  isRegistrar?: boolean;
  // When the case has a final decision, no court-application actions are allowed.
  disabled?: boolean;
};

// Forward chain: who can advance which status, and to what.
// pending → coordinator → registrar → sent_to_court → (court returns)
// returned_to_registrar → returned_to_coordinator → returned_to_prosecutor → completed
// The registrar uploads the Court Report File at returned_to_registrar (handled
// separately below), so it is not in this plain-advance map.
const forwardMap: Record<
  string,
  { role: "prosecutor" | "coordinator" | "registrar"; next: string }
> = {
  pending: { role: "prosecutor", next: "forwarded_to_coordinator" },
  // Responded back by the coordinator — the prosecutor revises and re-forwards.
  returned_to_prosecutor: {
    role: "prosecutor",
    next: "forwarded_to_coordinator",
  },
  forwarded_to_coordinator: {
    role: "coordinator",
    next: "forwarded_to_registrar",
  },
  returned_to_coordinator: {
    role: "coordinator",
    next: "returned_to_prosecutor",
  },
  forwarded_to_registrar: { role: "registrar", next: "sent_to_court" },
  // sent_to_court is handled by the registrar's Court Report block (which
  // advances to returned_to_coordinator), so it is not a plain advance.
};

// Respond-back (send back) chain.
const returnMap: Record<
  string,
  { role: "coordinator" | "registrar"; next: string }
> = {
  forwarded_to_coordinator: {
    role: "coordinator",
    next: "returned_to_prosecutor",
  },
  forwarded_to_registrar: { role: "registrar", next: "returned_to_coordinator" },
};

const CourtApplicationTab: React.FC<Props> = ({
  caseData,
  isProsecutor,
  isCoordinator,
  isRegistrar,
  disabled,
}) => {
  const { t } = useTranslation();
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();

  const [applications, setApplications] = useState<CourtApplication[]>([]);
  // Office stamp and signature printed on applications filed to court.
  const courtMarks = useCourtFilingMarks(
    !!isRegistrar &&
      !disabled &&
      applications.some((a) => a.status === "forwarded_to_registrar"),
  );
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [applicationFile, setApplicationFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedbackText, setFeedbackText] = useState<Record<string, string>>({});
  // Registrar's Court Report per application (filled when the case returns).
  const [courtReportData, setCourtReportData] = useState<
    Record<
      string,
      {
        decision: string;
        decision_date: string;
        file: File | null;
      }
    >
  >({});

  const getCourtReport = (app: CourtApplication) =>
    courtReportData[app.id || ""] || {
      decision: app.court_decision || "",
      decision_date: "",
      file: null,
    };

  const setCourtReportField = (
    app: CourtApplication,
    patch: Partial<{
      decision: string;
      decision_date: string;
      file: File | null;
    }>,
  ) =>
    setCourtReportData((p) => ({
      ...p,
      [app.id || ""]: { ...getCourtReport(app), ...patch },
    }));
  // Accordion: ids of expanded applications.
  const [openIds, setOpenIds] = useState<string[]>([]);
  // Prosecutor's in-place edits per application (used on respond-back revise).
  const [editData, setEditData] = useState<
    Record<string, { title: string; summary: string; file: File | null }>
  >({});

  const toggleOpen = (id: string) =>
    setOpenIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  // Registrar's court info per application (filled when sending to court).
  const [courtInfo, setCourtInfo] = useState<
    Record<
      string,
      {
        court_name: string;
        court_type: string;
        court_level: string;
        registrar_number: string;
      }
    >
  >({});

  const getCourtInfo = (app: CourtApplication) =>
    courtInfo[app.id || ""] || {
      court_name: app.court_name || "",
      court_type: app.court_type || "",
      court_level: app.court_level || "",
      registrar_number: app.registrar_number || "",
    };

  const setCourtInfoField = (
    app: CourtApplication,
    patch: Partial<{
      court_name: string;
      court_type: string;
      court_level: string;
      registrar_number: string;
    }>,
  ) =>
    setCourtInfo((p) => ({
      ...p,
      [app.id || ""]: { ...getCourtInfo(app), ...patch },
    }));

  const getEdit = (app: CourtApplication) =>
    editData[app.id || ""] || {
      title: app.title || "",
      summary: app.application_summary || "",
      file: null,
    };

  const setEdit = (
    app: CourtApplication,
    patch: Partial<{ title: string; summary: string; file: File | null }>,
  ) =>
    setEditData((p) => ({
      ...p,
      [app.id || ""]: { ...getEdit(app), ...patch },
    }));

  const role = isCoordinator
    ? "coordinator"
    : isRegistrar
    ? "registrar"
    : isProsecutor
    ? "prosecutor"
    : "";

  const baseUrl = `${process.env.REACT_APP_API_BASE_URL}${FILE_URL.COURT_APPLICATION}`;

  const load = () => {
    if (!caseData?.id) return;
    setLoading(true);
    fetchCourtApplications({ case_id: String(caseData.id) })
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setApplications(list);
        // Default-expand the most recently created application.
        const latest = [...list].sort(
          (a, b) =>
            new Date(b?.created_at || "").getTime() -
            new Date(a?.created_at || "").getTime(),
        )[0];
        if (latest?.id) setOpenIds([String(latest.id)]);
      })
      .catch(() => setApplications([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData?.id]);

  const handleCreate = () => {
    if (!title.trim()) {
      showTooltip(t("title_required", { defaultValue: "Title is required" }), "error");
      return;
    }
    if (!applicationFile) {
      showTooltip(
        t("application_file_required", {
          defaultValue: "Application file is required",
        }),
        "error",
      );
      return;
    }
    setSubmitting(true);
    addCourtApplication({
      case: String(caseData?.id),
      title,
      application_summary: summary,
      application_file: applicationFile,
      admin_unit_id: loginData?.admin_unit ? String(loginData.admin_unit) : null,
    })
      .then(() => {
        showTooltip(t("successful", { defaultValue: "Success" }), "success");
        setShowForm(false);
        setTitle("");
        setSummary("");
        setApplicationFile(null);
        load();
      })
      .catch((err) =>
        showTooltip(errorHandlerOnForm(err?.response?.data), "error"),
      )
      .finally(() => setSubmitting(false));
  };

  const advance = (app: CourtApplication) => {
    const step = forwardMap[app?.status || ""];
    if (!step) return;
    updateCourtApplication({ id: app.id, status: step.next as any })
      .then(() => {
        showTooltip(t("successful", { defaultValue: "Success" }), "success");
        load();
      })
      .catch((err) =>
        showTooltip(errorHandlerOnForm(err?.response?.data), "error"),
      );
  };

  const respondBack = (app: CourtApplication) => {
    const step = returnMap[app?.status || ""];
    if (!step) return;
    const comment = feedbackText[app.id || ""] || "";
    if (!comment.trim()) {
      showTooltip(
        t("feedback_required", { defaultValue: "Please enter a comment" }),
        "error",
      );
      return;
    }
    addCourtApplicationRespondBack({
      court_application: app.id,
      role,
      comment,
      status: "responded",
    })
      .then(() => updateCourtApplication({ id: app.id, status: step.next as any }))
      .then(() => {
        showTooltip(t("successful", { defaultValue: "Success" }), "success");
        setFeedbackText((p) => ({ ...p, [app.id || ""]: "" }));
        load();
      })
      .catch((err) =>
        showTooltip(errorHandlerOnForm(err?.response?.data), "error"),
      );
  };

  // Registrar uploads the Court Report File once the case returns from court,
  // then forwards to the coordinator.
  const submitCourtReport = (app: CourtApplication) => {
    const r = getCourtReport(app);
    if (!r.file) {
      showTooltip(
        t("court_report_file_required", {
          defaultValue: "Court report file is required",
        }),
        "error",
      );
      return;
    }
    editCourtApplication({
      id: app.id,
      court_decision: r.decision || undefined,
      court_decision_date: r.decision_date || undefined,
      court_report_file: r.file,
    })
      .then(() =>
        updateCourtApplication({
          id: app.id,
          status: "returned_to_coordinator",
        }),
      )
      .then(() => {
        showTooltip(t("successful", { defaultValue: "Success" }), "success");
        setCourtReportData((p) => {
          const next = { ...p };
          delete next[app.id || ""];
          return next;
        });
        load();
      })
      .catch((err) =>
        showTooltip(errorHandlerOnForm(err?.response?.data), "error"),
      );
  };

  // Prosecutor updates the application after a respond-back (optionally a new
  // application file), keeping the same returned_to_prosecutor status.
  const updateApplication = (app: CourtApplication) => {
    const e = getEdit(app);
    if (!e.title.trim()) {
      showTooltip(
        t("title_required", { defaultValue: "Title is required" }),
        "error",
      );
      return;
    }
    editCourtApplication({
      id: app.id,
      title: e.title,
      application_summary: e.summary,
      application_file: e.file || undefined,
    })
      .then(() => {
        showTooltip(t("successful", { defaultValue: "Success" }), "success");
        setEditData((p) => {
          const next = { ...p };
          delete next[app.id || ""];
          return next;
        });
        load();
      })
      .catch((err) =>
        showTooltip(errorHandlerOnForm(err?.response?.data), "error"),
      );
  };

  // Registrar submits court info and sends the application to court.
  const sendToCourt = (app: CourtApplication) => {
    const info = getCourtInfo(app);
    if (!info.court_name.trim()) {
      showTooltip(
        t("court_name_required", { defaultValue: "Court name is required" }),
        "error",
      );
      return;
    }
    updateCourtApplication(
      {
        id: app.id,
        court_name: info.court_name,
        court_type: info.court_type,
        court_level: info.court_level,
        registrar_number: info.registrar_number,
        status: "sent_to_court",
      },
      courtMarks.marks,
    )
      .then(() => {
        courtMarks.reset();
        showTooltip(t("successful", { defaultValue: "Success" }), "success");
        setCourtInfo((p) => {
          const next = { ...p };
          delete next[app.id || ""];
          return next;
        });
        load();
      })
      .catch((err) =>
        showTooltip(errorHandlerOnForm(err?.response?.data), "error"),
      );
  };

  const sendFeedback = (app: CourtApplication) => {
    const text = feedbackText[app.id || ""] || "";
    if (!text.trim()) {
      showTooltip(
        t("feedback_required", { defaultValue: "Please enter a comment" }),
        "error",
      );
      return;
    }
    addCourtApplicationFeedback({
      court_application: app.id,
      role,
      feedback_text: text,
    })
      .then(() => {
        showTooltip(t("successful", { defaultValue: "Success" }), "success");
        setFeedbackText((p) => ({ ...p, [app.id || ""]: "" }));
        load();
      })
      .catch((err) =>
        showTooltip(errorHandlerOnForm(err?.response?.data), "error"),
      );
  };

  const canForward = (app: CourtApplication) =>
    forwardMap[app?.status || ""]?.role === role;
  const canReturn = (app: CourtApplication) =>
    returnMap[app?.status || ""]?.role === role;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-2 h-8 bg-red-600 rounded-full"></div>
          <h3 className="text-lg font-semibold text-gray-900">
            {t("court_application", { defaultValue: "Court Application" })}
          </h3>
        </div>
        {isProsecutor && !disabled && (
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="px-5 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
          >
            + {t("add_court_application", { defaultValue: "Add Application" })}
          </button>
        )}
      </div>

      {showForm && (
        <div className="mb-6 rounded-xl border border-gray-200 bg-gray-50 p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label className="text-sm text-gray-600 mb-1">
                {t("title", { defaultValue: "Title" })}
                <span className="text-red-500"> *</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              />
            </div>
            <div className="flex flex-col md:col-span-2">
              <label className="text-sm text-gray-600 mb-1">
                {t("application_summary", { defaultValue: "Application Summary" })}
              </label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={3}
                className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              />
            </div>
            <FileUpload
              label={t("application_file", { defaultValue: "Application File" })}
              required
              fileTypes="*"
              onChange={setApplicationFile}
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={handleCreate}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60"
            >
              {submitting
                ? t("loading", { defaultValue: "Loading..." })
                : t("submit", { defaultValue: "Submit" })}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-10 text-gray-500">
          {t("loading", { defaultValue: "Loading..." })}
        </div>
      ) : applications.length === 0 ? (
        <div className="text-center py-10 text-gray-500 bg-gray-50 rounded-lg">
          {t("no_court_applications", {
            defaultValue: "No court applications yet",
          })}
        </div>
      ) : (
        <div className="space-y-4">
          {[...applications]
            .sort(
              (a, b) =>
                new Date(b?.created_at || "").getTime() -
                new Date(a?.created_at || "").getTime(),
            )
            .map((app) => {
              const isOpen = openIds.includes(String(app.id));
              return (
                <div
                  key={app.id}
                  className="border border-gray-200 rounded-xl bg-white overflow-hidden"
                >
                  {/* Accordion header (always visible) */}
                  <button
                    type="button"
                    onClick={() => toggleOpen(String(app.id))}
                    className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <div className="min-w-0">
                      <h4 className="text-base font-semibold text-gray-900 truncate">
                        {app.title}
                      </h4>
                      {app.created_at && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          {DateView(app.created_at)}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-800 whitespace-nowrap">
                        {t(`court_application_status_${app.status}`, {
                          defaultValue:
                            CourtApplicationStatus[
                              app.status as keyof typeof CourtApplicationStatus
                            ] || app.status,
                        })}
                      </span>
                      <svg
                        className={`w-4 h-4 shrink-0 text-gray-500 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </button>

                  {/* Accordion body */}
                  {isOpen && (
                    <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                      {app.application_summary && (
                        <p className="text-sm text-gray-600 whitespace-pre-line">
                          {app.application_summary}
                        </p>
                      )}

                      {Array.isArray(app.documents) &&
                        app.documents.length > 0 && (
                          <div className="mt-4">
                            <DocumentViewerModal
                              documents={app.documents as any}
                              baseUrl={baseUrl}
                              layout="list"
                              hideEmptyFile
                            />
                          </div>
                        )}

              {app.court_name && (
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    {
                      label: t("court_name", { defaultValue: "Court Name" }),
                      value: app.court_name,
                    },
                    {
                      label: t("court_type", { defaultValue: "Court Type" }),
                      value: app.court_type,
                    },
                    {
                      label: t("court_level", { defaultValue: "Court Level" }),
                      value: app.court_level,
                    },
                    {
                      label: t("registrar_number", {
                        defaultValue: "Registrar Number",
                      }),
                      value: app.registrar_number,
                    },
                  ]
                    .filter((f) => f.value)
                    .map((f, i) => (
                      <div key={i} className="space-y-0.5">
                        <p className="text-xs font-medium text-gray-500">
                          {f.label}
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                          {f.value}
                        </p>
                      </div>
                    ))}
                </div>
              )}

              {(app.court_decision || app.court_decision_date) && (
                <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
                  <p className="text-xs font-semibold uppercase text-gray-500">
                    {t("court_report", { defaultValue: "Court Report" })}
                  </p>
                  {app.court_decision && (
                    <div>
                      <p className="text-xs font-medium text-gray-500">
                        {t("court_decision", { defaultValue: "Court Decision" })}
                      </p>
                      <p className="text-sm text-gray-800 whitespace-pre-line">
                        {app.court_decision}
                      </p>
                    </div>
                  )}
                  {app.court_decision_date && (
                    <div>
                      <p className="text-xs font-medium text-gray-500">
                        {t("court_decision_date", {
                          defaultValue: "Court Decision Date",
                        })}
                      </p>
                      <p className="text-sm text-gray-800">
                        {DateView(app.court_decision_date)}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {Array.isArray(app.feedbacks) && app.feedbacks.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs font-semibold uppercase text-gray-500">
                    {t("feedbacks_title", { defaultValue: "Feedbacks" })}
                  </p>
                  {app.feedbacks.map((f, i) => (
                    <div
                      key={i}
                      className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg p-2"
                    >
                      {f.feedback_text}
                    </div>
                  ))}
                </div>
              )}

              {/* Respond-back comments (e.g. coordinator sending it back) */}
              {Array.isArray(app.respond_backs) &&
                app.respond_backs.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs font-semibold uppercase text-amber-700">
                      {t("respond_back_comments", {
                        defaultValue: "Respond-back Comments",
                      })}
                    </p>
                    {app.respond_backs.map((r, i) => (
                      <div
                        key={i}
                        className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2"
                      >
                        {r.comment}
                        {r.role ? (
                          <span className="block text-xs text-amber-600 mt-1">
                            — {r.role}
                          </span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}

              {/* Prosecutor revises after a respond-back: edit/update, view the
                  latest file, then re-forward to the coordinator. */}
              {isProsecutor &&
                !disabled &&
                app.status === "returned_to_prosecutor" && (
                <div className="mt-4 pt-4 border-t border-amber-100 space-y-3">
                  <p className="text-sm font-semibold text-amber-800">
                    ↩️{" "}
                    {t("update_and_reforward_hint", {
                      defaultValue:
                        "The coordinator responded back. Review the feedback, update the application, then re-forward for approval.",
                    })}
                  </p>
                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600 mb-1">
                      {t("title", { defaultValue: "Title" })}
                    </label>
                    <input
                      type="text"
                      value={getEdit(app).title}
                      onChange={(e) => setEdit(app, { title: e.target.value })}
                      className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600 mb-1">
                      {t("application_summary", {
                        defaultValue: "Application Summary",
                      })}
                    </label>
                    <textarea
                      value={getEdit(app).summary}
                      onChange={(e) => setEdit(app, { summary: e.target.value })}
                      rows={3}
                      className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    />
                  </div>
                  <FileUpload
                    label={t("application_file", {
                      defaultValue: "Application File",
                    })}
                    fileTypes="*"
                    onChange={(file) => setEdit(app, { file })}
                  />
                  <div className="flex flex-wrap justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => updateApplication(app)}
                      className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium"
                    >
                      {t("update", { defaultValue: "Update" })}
                    </button>
                    <button
                      type="button"
                      onClick={() => advance(app)}
                      className="px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
                    >
                      {t("reforward_to_coordinator", {
                        defaultValue: "Re-forward to Coordinator",
                      })}
                    </button>
                  </div>
                </div>
              )}

              {/* Registrar fills court info and sends the application to court. */}
              {isRegistrar &&
                !disabled &&
                app.status === "forwarded_to_registrar" && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                  <p className="text-sm font-semibold text-gray-800">
                    {t("court_info", { defaultValue: "Court Info" })}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex flex-col">
                      <label className="text-sm text-gray-600 mb-1">
                        {t("court_name", { defaultValue: "Court Name" })}
                        <span className="text-red-500"> *</span>
                      </label>
                      <input
                        type="text"
                        value={getCourtInfo(app).court_name}
                        onChange={(e) =>
                          setCourtInfoField(app, { court_name: e.target.value })
                        }
                        className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm text-gray-600 mb-1">
                        {t("court_type", { defaultValue: "Court Type" })}
                      </label>
                      <input
                        type="text"
                        value={getCourtInfo(app).court_type}
                        onChange={(e) =>
                          setCourtInfoField(app, { court_type: e.target.value })
                        }
                        className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm text-gray-600 mb-1">
                        {t("court_level", { defaultValue: "Court Level" })}
                      </label>
                      <input
                        type="text"
                        value={getCourtInfo(app).court_level}
                        onChange={(e) =>
                          setCourtInfoField(app, { court_level: e.target.value })
                        }
                        className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="text-sm text-gray-600 mb-1">
                        {t("registrar_number", {
                          defaultValue: "Registrar Number",
                        })}
                      </label>
                      <input
                        type="text"
                        value={getCourtInfo(app).registrar_number}
                        onChange={(e) =>
                          setCourtInfoField(app, {
                            registrar_number: e.target.value,
                          })
                        }
                        className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                      />
                    </div>
                  </div>
                  {courtMarks.fields}
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => sendToCourt(app)}
                      className="px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
                    >
                      {t("send_to_court", { defaultValue: "Send to Court" })}
                    </button>
                  </div>
                </div>
              )}

              {/* Registrar uploads the Court Report (decision + file) while the
                  application is at court. */}
              {isRegistrar && !disabled && app.status === "sent_to_court" && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                  <p className="text-sm font-semibold text-gray-800">
                    {t("court_report", { defaultValue: "Court Report" })}
                  </p>
                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600 mb-1">
                      {t("court_decision", { defaultValue: "Court Decision" })}
                    </label>
                    <textarea
                      value={getCourtReport(app).decision}
                      onChange={(e) =>
                        setCourtReportField(app, { decision: e.target.value })
                      }
                      rows={3}
                      className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-sm text-gray-600 mb-1">
                      {t("court_decision_date", {
                        defaultValue: "Court Decision Date",
                      })}
                    </label>
                    <input
                      type="datetime-local"
                      value={getCourtReport(app).decision_date}
                      onChange={(e) =>
                        setCourtReportField(app, {
                          decision_date: e.target.value,
                        })
                      }
                      className="bg-white border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    />
                  </div>
                  <FileUpload
                    label={t("court_report_file", {
                      defaultValue: "Court Report File",
                    })}
                    required
                    fileTypes="*"
                    onChange={(file) => setCourtReportField(app, { file })}
                  />
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => submitCourtReport(app)}
                      className="px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
                    >
                      {t("submit_court_report", {
                        defaultValue: "Submit Court Report & Forward",
                      })}
                    </button>
                  </div>
                </div>
              )}

              {/* Workflow + feedback actions (the revise block above handles
                  the returned_to_prosecutor re-forward). */}
              {!disabled &&
                (canForward(app) || canReturn(app)) &&
                !(
                  isProsecutor && app.status === "returned_to_prosecutor"
                ) && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                  {((canReturn(app) && !isRegistrar) || isCoordinator) && (
                    <textarea
                      value={feedbackText[app.id || ""] || ""}
                      onChange={(e) =>
                        setFeedbackText((p) => ({
                          ...p,
                          [app.id || ""]: e.target.value,
                        }))
                      }
                      rows={2}
                      placeholder={t("write_a_comment", {
                        defaultValue: "Write a comment...",
                      })}
                      className="w-full bg-white border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring focus:ring-blue-300"
                    />
                  )}
                  <div className="flex flex-wrap gap-3">
                    {canForward(app) &&
                      // The registrar's "Send to Court" block above replaces the
                      // generic Forward at forwarded_to_registrar.
                      !(
                        isRegistrar &&
                        app.status === "forwarded_to_registrar"
                      ) && (
                        <button
                          type="button"
                          onClick={() => advance(app)}
                          className="px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
                        >
                          {t("forward", { defaultValue: "Forward" })}
                        </button>
                      )}
                    {canReturn(app) && !isRegistrar && (
                      <button
                        type="button"
                        onClick={() => respondBack(app)}
                        className="px-5 py-2 border border-amber-300 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 text-sm font-medium"
                      >
                        ↩️ {t("respond_back", { defaultValue: "Respond Back" })}
                      </button>
                    )}
                    {isCoordinator && (
                      <button
                        type="button"
                        onClick={() => sendFeedback(app)}
                        className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium"
                      >
                        {t("add_feedback", { defaultValue: "Add Feedback" })}
                      </button>
                    )}
                  </div>
                </div>
              )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default CourtApplicationTab;
